import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { appErrorMonitorService, AppErrorEvent } from '../services/appErrorMonitorService';
import {
  aggregateCrashGroups,
  getCrashEventKind,
  getCrashLastScreen,
  summarizeCrashCounts,
  type CrashEventKind,
  type CrashGroup,
} from '../utils/crashMonitor';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.55rem 0.75rem;
`;

const ToolbarCluster = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
`;

const ToolbarSpacer = styled.div`
  flex: 1;
  min-width: 0.5rem;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 0.9rem;
  border-radius: 10px;
  border: 0;
  background: var(--primary);
  color: var(--onPrimary);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.45rem 0.65rem;
  font-size: 0.82rem;
`;

const Feedback = styled.div<{ $tone: 'error' | 'warning' | 'info' }>`
  border-radius: 12px;
  padding: 0.65rem 0.85rem;
  font-size: 0.82rem;
  color: ${({ $tone }) =>
    $tone === 'error' ? 'var(--error)' : $tone === 'warning' ? 'var(--warning)' : 'var(--info)'};
  background: color-mix(
    in srgb,
    ${({ $tone }) =>
        $tone === 'error' ? 'var(--error)' : $tone === 'warning' ? 'var(--warning)' : 'var(--info)'}
      12%,
    var(--surface)
  );
  border: 1px solid
    color-mix(
      in srgb,
      ${({ $tone }) =>
          $tone === 'error' ? 'var(--error)' : $tone === 'warning' ? 'var(--warning)' : 'var(--info)'}
        28%,
      var(--outline)
    );
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const StatTile = styled.div`
  border-radius: 14px;
  border: 1px solid var(--outline);
  background: var(--surface);
  padding: 0.85rem 0.95rem;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: var(--onSurfaceVariant);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const StatValue = styled.div<{ $bad?: boolean }>`
  margin-top: 0.25rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: ${({ $bad }) => ($bad ? 'var(--error)' : 'var(--onSurface)')};
`;

const TableShell = styled.div`
  overflow: auto;
  border: 1px solid var(--outline);
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;

  th,
  td {
    text-align: left;
    padding: 0.65rem 0.75rem;
    border-bottom: 1px solid var(--outline);
    vertical-align: top;
  }

  th {
    color: var(--onSurfaceVariant);
    font-weight: 600;
  }
`;

const DetailButton = styled.button`
  background: none;
  border: 0;
  padding: 0;
  color: var(--primary);
  font: inherit;
  cursor: pointer;
  text-align: left;
`;

const EmptyState = styled.div`
  border-radius: 14px;
  border: 1px dashed var(--outline);
  padding: 1.5rem;
  color: var(--onSurfaceVariant);
`;

const KindBadge = styled.span<{ $kind: CrashEventKind }>`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--onError);
  background: ${({ $kind }) =>
    $kind === 'restart_suppressed' ? 'var(--info)' : $kind === 'restart_app' ? 'var(--warning)' : 'var(--error)'};
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
`;

const ModalCard = styled.div`
  width: min(720px, calc(100vw - 2rem));
  max-height: calc(100vh - 2rem);
  overflow: auto;
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 16px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CodeBlock = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.75rem;
  background: color-mix(in srgb, var(--surfaceVariant) 40%, var(--surface));
  border-radius: 10px;
  padding: 0.75rem;
`;

const compactId = (value?: string | null) => {
  if (!value) return '';
  return value.length > 12 ? `${value.slice(0, 8)}…` : value;
};

const formatJson = (value: unknown) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const SuperAdminCrashesTab: React.FC = () => {
  const [events, setEvents] = useState<AppErrorEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [platform, setPlatform] = useState('all');
  const [kindFilter, setKindFilter] = useState<'all' | CrashEventKind>('all');
  const [selected, setSelected] = useState<CrashGroup | null>(null);

  const loadCrashes = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await appErrorMonitorService.getCrashEvents(500, {
      platform: platform === 'all' ? undefined : platform,
    });
    if (!result.success) {
      setError(result.message || 'Failed to load crash events.');
      setEvents([]);
    } else {
      setEvents(result.data ?? []);
    }
    setLoading(false);
  }, [platform]);

  useEffect(() => {
    void loadCrashes();
  }, [loadCrashes]);

  const groups = useMemo(() => {
    const aggregated = aggregateCrashGroups(events);
    if (kindFilter === 'all') return aggregated;
    return aggregated.filter((group) => group.eventKind === kindFilter);
  }, [events, kindFilter]);

  const counts = useMemo(() => summarizeCrashCounts(events), [events]);

  return (
    <Stack>
      <Toolbar>
        <ToolbarCluster>
          <PrimaryButton disabled={loading} onClick={() => void loadCrashes()}>
            {loading ? 'Refreshing' : 'Refresh'}
          </PrimaryButton>
          <Select
            aria-label="Filter crashes by platform"
            value={platform}
            onChange={(event) => setPlatform(event.target.value)}
          >
            <option value="all">All platforms</option>
            <option value="android">Android</option>
            <option value="ios">iOS</option>
            <option value="web">Web</option>
          </Select>
          <Select
            aria-label="Filter crashes by kind"
            value={kindFilter}
            onChange={(event) => setKindFilter(event.target.value as 'all' | CrashEventKind)}
          >
            <option value="all">All crash kinds</option>
            <option value="js_fatal">Fatal JS</option>
            <option value="dirty_exit">Dirty exits</option>
            <option value="restart_app">Restarts</option>
          </Select>
        </ToolbarCluster>
        <ToolbarSpacer />
      </Toolbar>

      {error ? <Feedback $tone="error">{error}</Feedback> : null}
      {!error && !loading && groups.length === 0 ? (
        <Feedback $tone="info">No crash-like events recorded for the current filters.</Feedback>
      ) : null}

      <StatsGrid>
        <StatTile>
          <StatLabel>Crashes (24h)</StatLabel>
          <StatValue $bad={counts.total > 0}>{counts.total}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel>Fatal JS (24h)</StatLabel>
          <StatValue $bad={counts.fatalJs > 0}>{counts.fatalJs}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel>Dirty exits (24h)</StatLabel>
          <StatValue $bad={counts.dirtyExits > 0}>{counts.dirtyExits}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel>Restarts (24h)</StatLabel>
          <StatValue $bad={counts.restarts > 0}>{counts.restarts}</StatValue>
        </StatTile>
      </StatsGrid>

      {groups.length === 0 && !loading ? (
        <EmptyState>No grouped crashes yet. Open the mobile app or wait for the next dirty-exit report.</EmptyState>
      ) : (
        <TableShell aria-busy={loading}>
          <Table>
            <thead>
              <tr>
                <th>Last seen</th>
                <th>Kind</th>
                <th>Message</th>
                <th>Last screen</th>
                <th>Platform</th>
                <th>Version</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.fingerprint}>
                  <td>{new Date(group.lastSeenAt).toLocaleString()}</td>
                  <td>
                    <KindBadge $kind={group.eventKind}>{group.eventKind}</KindBadge>
                  </td>
                  <td>
                    <DetailButton onClick={() => setSelected(group)}>
                      {group.message.length > 120 ? `${group.message.slice(0, 120)}...` : group.message}
                    </DetailButton>
                  </td>
                  <td>{group.lastScreen ?? ''}</td>
                  <td>{group.platforms.join(', ')}</td>
                  <td>{group.versions.join(', ')}</td>
                  <td>{group.count}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableShell>
      )}

      {selected ? (
        <Overlay onClick={() => setSelected(null)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <div>
              <strong>{selected.message}</strong>
            </div>
            <div>Kind: {getCrashEventKind(selected.sample)}</div>
            <div>Last screen: {getCrashLastScreen(selected.sample) ?? 'unknown'}</div>
            <div>User: {compactId(selected.sample.user_id)}</div>
            <div>Session: {compactId(selected.sample.session_id)}</div>
            <div>Source: {selected.sample.source ?? ''}</div>
            <div>Platform / version: {[selected.sample.platform, selected.sample.app_version].filter(Boolean).join(' / ')}</div>
            <div>
              Stack
              <CodeBlock>{selected.sample.stack || 'No stack captured.'}</CodeBlock>
            </div>
            <div>
              Context
              <CodeBlock>{formatJson(selected.sample.context)}</CodeBlock>
            </div>
            <div>
              Tags
              <CodeBlock>{formatJson(selected.sample.tags)}</CodeBlock>
            </div>
            <PrimaryButton type="button" onClick={() => setSelected(null)}>
              Close
            </PrimaryButton>
          </ModalCard>
        </Overlay>
      ) : null}
    </Stack>
  );
};
